import { useEffect, useRef, useState } from 'preact/hooks';
import { defCoursesRoute } from '@course/components/courses-app-router';
import { LoadedProps, RouteLoadProps, errRedirect } from '@components/router';
import { rpx } from 'client/lib/rpx-client';
import { AipFoundingRatePrice } from 'client/courses/components/aip-founding-rate-price';
import { MiraseeLogo } from '@course/components/mirasee-logo';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { PriceRow } from 'server/types';
import * as pmtmath from 'shared/payments/math';
import { StripeContext, StripeInput } from '@course/components/checkout/stripe';
import { processStripePayment } from '@course/components/checkout/stripe-checkout-form';
import {
  describeCheckoutError,
  loggableCheckoutError,
} from '@course/components/checkout/checkout-logic';
import { reportCheckoutError } from '@course/components/checkout/report-checkout-error';
import { useAuth } from 'client/lib/auth';
import { showError } from '@components/app-error';
import * as pmtfmt from 'shared/payments/fmt';
import { detectTimezone } from 'shared/dateutil';
import { Currency } from 'server/types';
import { SetPasswordForm } from '@course/components/checkout/set-password-form';
import { ERROR_CODES } from 'shared/consts';

const LOGIN_URL = `/login?redirect=${encodeURIComponent('/community/join')}`;

async function load({ auth }: RouteLoadProps) {
  if (!auth.user) {
    return loadGuest();
  }

  const access = await rpx.miraseeCommunity
    .getAipAccessState()
    .catch(() => ({ isMember: false, hadMembership: false, pendingAccessDate: undefined }));
  if (access.isMember) {
    // Members have nothing to buy here.
    throw errRedirect('/community');
  }

  if (access.pendingAccessDate) {
    // Already bought, waiting on the cohort to open. Selling it again here
    // would take a second payment for the same thing.
    throw errRedirect('/community/courses');
  }

  try {
    const { price, requiresAddress, savedAddress, currencyOptions } =
      await rpx.aipUpgrade.getAipUpgradeState({
        checkAddress: true,
        // The server prefers the country its edge gives it and falls back to
        // this. Either way it only picks which currency shows first.
        timezone: detectTimezone(),
      });
    return {
      hadMembership: access.hadMembership,
      aipPrice: price as PriceRow,
      aipRequiresAddress: requiresAddress,
      aipSavedAddress: savedAddress,
      aipCurrencyOptions: currencyOptions,
    };
  } catch {
    return {
      hadMembership: access.hadMembership,
      aipPrice: undefined,
      aipRequiresAddress: undefined,
      aipSavedAddress: undefined,
      aipCurrencyOptions: [],
    };
  }
}

/**
 * A visitor with no account sees the standard offer. They create an account
 * on this page before the card form opens.
 */
async function loadGuest() {
  try {
    const { price, currencyOptions } = await rpx.aipUpgrade.getAipGuestOffer({
      timezone: detectTimezone(),
    });
    return {
      hadMembership: false,
      aipPrice: price as PriceRow,
      aipRequiresAddress: true as boolean | undefined,
      aipSavedAddress: undefined as Record<string, string | undefined> | undefined,
      aipCurrencyOptions: currencyOptions,
    };
  } catch {
    return {
      hadMembership: false,
      aipPrice: undefined,
      aipRequiresAddress: undefined,
      aipSavedAddress: undefined,
      aipCurrencyOptions: [],
    };
  }
}

function currencyOptionLabel(option: {
  currency: Currency;
  priceInCents: number;
  interval?: string;
}): string {
  const amount = pmtfmt.price({
    priceInCents: option.priceInCents,
    currency: option.currency,
    explicitCurrency: true,
  });
  const interval = option.interval ? `/${option.interval}` : '';
  const name = option.currency === 'CAD' ? 'Canadian dollars' : 'US dollars';
  return `${name} — ${amount}${interval}`;
}

function formatTrialPeriod(days: number): string {
  if (days % 7 === 0) {
    const weeks = days / 7;
    return `${weeks} week${weeks === 1 ? '' : 's'}`;
  }
  return `${days} day${days === 1 ? '' : 's'}`;
}

function trialEndDate(days: number): string {
  const end = new Date();
  end.setDate(end.getDate() + days);
  return end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function CardIcon({ brand }: { brand: string }) {
  const b = brand.toLowerCase();
  if (b === 'visa') {
    return (
      <svg
        width="40"
        height="26"
        viewBox="0 0 40 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="40" height="26" rx="4" fill="#1A1F71" />
        <path
          d="M17.2 17.5l1.8-10.9h2.8l-1.8 10.9H17.2zm11.6-10.6c-.6-.2-1.4-.4-2.5-.4-2.8 0-4.7 1.5-4.7 3.6 0 1.6 1.4 2.4 2.5 3 1.1.5 1.5.9 1.5 1.4 0 .7-.9 1.1-1.7 1.1-1.1 0-1.8-.2-2.7-.6l-.4-.2-.4 2.5c.7.3 1.9.6 3.2.6 3 0 4.9-1.5 4.9-3.7 0-1.2-.7-2.2-2.4-3-.9-.5-1.5-.8-1.5-1.3 0-.4.5-.9 1.5-.9.9 0 1.5.2 2 .4l.2.1.5-2.6zM34.3 6.6h-2.2c-.7 0-1.2.2-1.5.9l-4.2 10h3l.6-1.6h3.6l.3 1.6H36l-2-10.9h.3zm-3.5 7l1.5-4 .8 4h-2.3zM15.4 6.6L12.6 14l-.3-1.5c-.5-1.8-2.2-3.7-4-4.6l2.5 9.5h3l4.5-10.8h-2.9z"
          fill="#fff"
        />
        <path
          d="M10 6.6H5.6l-.1.3c3.5.9 5.9 3.1 6.8 5.7L11.4 7.5c-.2-.7-.7-.9-1.4-.9z"
          fill="#F9A533"
        />
      </svg>
    );
  }
  if (b === 'mastercard') {
    return (
      <svg
        width="40"
        height="26"
        viewBox="0 0 40 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="40" height="26" rx="4" fill="#252525" />
        <circle cx="16" cy="13" r="7" fill="#EB001B" />
        <circle cx="24" cy="13" r="7" fill="#F79E1B" />
        <path
          d="M20 7.7a7 7 0 0 1 2.6 5.3A7 7 0 0 1 20 18.3a7 7 0 0 1-2.6-5.3A7 7 0 0 1 20 7.7z"
          fill="#FF5F00"
        />
      </svg>
    );
  }
  if (b === 'amex' || b === 'american_express') {
    return (
      <svg
        width="40"
        height="26"
        viewBox="0 0 40 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="40" height="26" rx="4" fill="#2E77BC" />
        <text
          x="20"
          y="16"
          text-anchor="middle"
          fill="#fff"
          font-size="8"
          font-weight="bold"
          font-family="sans-serif"
        >
          AMEX
        </text>
      </svg>
    );
  }
  if (b === 'discover') {
    return (
      <svg
        width="40"
        height="26"
        viewBox="0 0 40 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="40" height="26" rx="4" fill="#fff" stroke="#e2e8f0" />
        <text
          x="20"
          y="15"
          text-anchor="middle"
          fill="#FF6600"
          font-size="7"
          font-weight="bold"
          font-family="sans-serif"
        >
          DISCOVER
        </text>
      </svg>
    );
  }
  return (
    <svg width="40" height="26" viewBox="0 0 40 26" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="26" rx="4" fill="#f3f4f6" stroke="#e2e8f0" />
      <rect x="8" y="7" width="24" height="4" rx="1" fill="#d1d5db" />
      <rect x="8" y="15" width="12" height="3" rx="1" fill="#d1d5db" />
    </svg>
  );
}

function ConfirmModal({
  isProcessing,
  onClose,
  children,
}: {
  isProcessing: boolean;
  onClose: () => void;
  children: preact.ComponentChildren;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isProcessing) {
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isProcessing, onClose]);

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-heading"
      style="background: rgba(0,0,0,0.5)"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isProcessing) {
          onClose();
        }
      }}
    >
      <div class="bg-white rounded-2xl p-8 max-w-md w-[calc(100%-40px)] shadow-xl text-center">
        {children}
      </div>
    </div>
  );
}

/**
 * The join endpoints answer with Boom messages written for an API, not for the
 * person reading them: "You are already an AIP member.", `Invalid purchase
 * status "active"`. Say what happened and where to go next instead.
 */
function describeJoinError(err: any): { message: string; communityLink?: boolean } {
  // An rpx schema failure carries its detail in data.errors, and data.message
  // is the literal string "Validation failed" -- useless under a card form.
  const validationMessage: string | undefined = err?.data?.errors?.[0]?.message;
  if (validationMessage) {
    return { message: validationMessage };
  }

  const msg: string = err?.data?.message || err?.message || '';

  if (/already an AIP member/i.test(msg)) {
    return { message: 'You already have an AI Playground membership.', communityLink: true };
  }
  if (err?.data?.code === 'ALREADY_ACTIVE' || /invalid purchase status/i.test(msg)) {
    return { message: 'Your membership is already active.', communityLink: true };
  }
  if (/access opens on/i.test(msg)) {
    // Already a finished sentence naming the date.
    return { message: msg };
  }
  if (/currently full|enrollment is full/i.test(msg)) {
    return {
      message:
        'AI Playground is at capacity right now. Email support@mirasee.com and we will tell you when a place opens.',
    };
  }
  return { message: msg || 'Something went wrong. Please try again.' };
}

type CardInfo =
  | { hasCard: true; last4: string; brand: string; currency: Currency }
  | { hasCard: false };

type CurrencyOption = { currency: Currency; priceInCents: number; interval?: string };

function NotEnrolledPage({
  aipPrice: loadedPrice,
  requiresAddress: loadedRequiresAddress,
  savedAddress: loadedSavedAddress,
  hadMembership,
  currencyOptions,
}: {
  aipPrice?: PriceRow;
  requiresAddress?: boolean;
  savedAddress?: Record<string, string | undefined>;
  hadMembership?: boolean;
  currencyOptions?: CurrencyOption[];
}) {
  const joinVerb = hadMembership ? 'Rejoin' : 'Join';
  useDocumentTitle([`${joinVerb} AI Playground`]);
  const auth = useAuth();
  const user = auth.user;
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [accountError, setAccountError] = useState<
    { message: string; loginLink?: boolean } | undefined
  >(undefined);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const creatingAccountRef = useRef(false);
  // Accounts made on this page start with a random password. Ask for a real
  // one after payment, the way campaign checkout does.
  const [createdAccount, setCreatedAccount] = useState(false);
  const [askForPassword, setAskForPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmError, setConfirmError] = useState<
    { message: string; communityLink?: boolean } | undefined
  >(undefined);
  const [cardError, setCardError] = useState('');
  const [joined, setJoined] = useState(false);
  const [cardInfo, setCardInfo] = useState<CardInfo | undefined>(undefined);
  // The offer currently on screen. It changes when the buyer picks a different
  // currency, which re-reads the price, the address requirement and the saved
  // address together — they all belong to the price being sold.
  const [offer, setOffer] = useState({
    price: loadedPrice,
    requiresAddress: loadedRequiresAddress,
    savedAddress: loadedSavedAddress,
  });
  const [switchingCurrency, setSwitchingCurrency] = useState(false);
  const [currencyError, setCurrencyError] = useState('');
  const aipPrice = offer.price;
  const requiresAddress = offer.requiresAddress;
  const savedAddress = offer.savedAddress;
  const options = currencyOptions || [];
  // The server checks the saved card against one currency at a time: Stripe
  // will not bill a customer in a new currency while a subscription in the
  // old one is still live. Only trust an answer about the price on screen.
  const canOneClick = !!cardInfo?.hasCard && cardInfo.currency === offer.price?.currency;
  const [stripeCtx, setStripeCtx] = useState<StripeContext | undefined>(undefined);
  const processingRef = useRef(false);

  useEffect(() => {
    if (!user) {
      return;
    }
    rpx.aipUpgrade
      .getAipCardInfo({ currency: loadedPrice?.currency })
      .then(setCardInfo)
      .catch(() => {});
  }, []);

  // Spell the currency out whenever there is more than one on offer: two bare
  // "$" prices side by side say nothing about which is which.
  const explicitCurrency = options.length > 1;
  const priceDisplay = aipPrice ? pmtfmt.price({ ...aipPrice, explicitCurrency }) : '';
  const zeroDisplay = aipPrice
    ? pmtfmt.price({ priceInCents: 0, currency: aipPrice.currency, explicitCurrency })
    : '';

  async function selectCurrency(currency: Currency) {
    if (currency === aipPrice?.currency || switchingCurrency) {
      return;
    }
    // StripeInput builds its Payment Element around one price and keys only on
    // the price id; swapping that underneath it mounts a second element on top
    // of the first, and the buyer can end up typing into the stale one. Close
    // the form so it unmounts, and let them reopen it on the new price.
    setShowCardForm(false);
    setStripeCtx(undefined);
    setCardError('');
    setSwitchingCurrency(true);
    setCurrencyError('');
    try {
      if (!user) {
        const guestOffer = await rpx.aipUpgrade.getAipGuestOffer({ currency });
        setOffer({
          price: guestOffer.price as PriceRow,
          requiresAddress: true,
          savedAddress: undefined,
        });
        return;
      }
      const next = await rpx.aipUpgrade.getAipUpgradeState({ checkAddress: true, currency });
      setOffer({
        price: next.price as PriceRow,
        requiresAddress: next.requiresAddress,
        savedAddress: next.savedAddress,
      });
      // Re-check the saved card for the currency now on screen. Usually it
      // can pay in either; when it cannot, the card form takes over.
      const card = await rpx.aipUpgrade
        .getAipCardInfo({ currency: next.price?.currency })
        .catch(() => undefined);
      setCardInfo(card);
    } catch (err) {
      setCurrencyError('We could not switch currency. Please try again.');
      console.warn('aip join currency switch failed', err);
    } finally {
      setSwitchingCurrency(false);
    }
  }
  const interval =
    aipPrice?.paymentType === 'subscription' && aipPrice.interval ? `/${aipPrice.interval}` : '';
  const freeTrialDays = aipPrice ? pmtmath.freeTrialPeriod({ price: aipPrice }) : 0;

  const [purchased, setPurchased] = useState(false);

  async function handleCreateAccount(e: Event) {
    e.preventDefault();
    if (creatingAccountRef.current) {
      return;
    }
    const name = accountName.trim();
    const email = accountEmail.trim();
    if (!name || !email) {
      setAccountError({ message: 'Enter your name and email.' });
      return;
    }
    creatingAccountRef.current = true;
    setCreatingAccount(true);
    setAccountError(undefined);
    try {
      const newUser = await rpx.auth.registerNewUserForCheckout({
        name,
        email,
        timezone: detectTimezone(),
      });
      await auth.login(Promise.resolve({ ...newUser, level: 'student' as const }));
    } catch (err: any) {
      if (err?.data?.code === ERROR_CODES.userExists) {
        setAccountError({
          message: 'This email already has an account. Log in to continue.',
          loginLink: true,
        });
      } else {
        const validationMessage: string | undefined = err?.data?.errors?.[0]?.message;
        setAccountError({
          message:
            validationMessage ||
            err?.data?.message ||
            'We could not create your account. Please try again.',
        });
      }
      console.warn('aip join account creation failed', loggableCheckoutError(err));
      creatingAccountRef.current = false;
      setCreatingAccount(false);
      return;
    }
    setCreatedAccount(true);
    // Now that there is an account, read the offer the way a logged-in buyer
    // gets it, keeping the currency they picked. A brand-new account has no
    // cohort deal, so if this fails the standard offer on screen still holds.
    try {
      const next = await rpx.aipUpgrade.getAipUpgradeState({
        checkAddress: true,
        currency: aipPrice?.currency,
      });
      setOffer({
        price: next.price as PriceRow,
        requiresAddress: next.requiresAddress,
        savedAddress: next.savedAddress,
      });
    } catch (err) {
      console.warn('aip join offer refresh failed', err);
    }
    setShowAccountForm(false);
    setShowCardForm(true);
    creatingAccountRef.current = false;
    setCreatingAccount(false);
  }

  async function handleConfirm() {
    setIsProcessing(true);
    setConfirmError(undefined);
    try {
      if (!purchased) {
        const result = await rpx.aipUpgrade.purchaseAipOneClick({
          currency: aipPrice?.currency,
        });
        if (result.type === 'action') {
          setConfirmError({
            message: 'Your bank needs to verify this payment. Email support@mirasee.com to finish.',
          });
          setIsProcessing(false);
          return;
        }
        setPurchased(true);
      }
      await rpx.aipUpgrade.provisionAipUpgrade();
      setJoined(true);
      setTimeout(() => {
        window.location.assign('/community');
      }, 1500);
    } catch (err: any) {
      const msg = err?.data?.message || err?.message || '';
      if (msg.includes('No saved payment method')) {
        setShowConfirm(false);
        setConfirmError(undefined);
        setIsProcessing(false);
        setShowCardForm(true);
        return;
      }
      setConfirmError(describeJoinError(err));
      setIsProcessing(false);
    }
  }

  async function handleCardSubmit() {
    if (!stripeCtx || !aipPrice || !user || processingRef.current) {
      return;
    }
    processingRef.current = true;
    setIsProcessing(true);
    setCardError('');
    try {
      const result = await processStripePayment({
        ctx: stripeCtx,
        user,
        priceId: aipPrice.id,
        fallbackAddress: savedAddress,
        processPayment: async (opts) => {
          return rpx.aipUpgrade.purchaseAipUpgrade({
            address: opts.address,
            currency: aipPrice.currency,
          });
        },
      });
      if (!result || result.type === 'validationError') {
        // An incomplete Address Element shows no error of its own, so without
        // a message the button just resets and the click looks ignored.
        setCardError(
          result
            ? 'Please fill in every field above, then try again.'
            : 'We could not save your card. Please try again.',
        );
        processingRef.current = false;
        setIsProcessing(false);
        return;
      }
      try {
        await rpx.aipUpgrade.provisionAipUpgrade();
      } catch {
        showError({
          title: 'Membership activation delayed',
          error:
            'Your payment was successful but we had trouble activating your membership. ' +
            'Please refresh the page — your access should be ready shortly.',
        });
        if (createdAccount) {
          setAskForPassword(true);
          return;
        }
        window.location.assign('/community');
        return;
      }
      if (createdAccount) {
        setAskForPassword(true);
        return;
      }
      setJoined(true);
      setTimeout(() => {
        window.location.assign('/community');
      }, 1500);
    } catch (err) {
      const described = describeCheckoutError(err);
      reportCheckoutError({ step: 'checkout', priceId: aipPrice?.id, err });
      if (described.kind === 'card') {
        setCardError(described.message);
        console.warn('aip community upgrade card error', loggableCheckoutError(err));
      } else {
        // A Boom.conflict from the join guards lands here as kind 'other'.
        // Those belong in the form, in words, not in a generic error dialog --
        // but they still belong in the console, which showError used to do.
        console.warn('aip community join error', loggableCheckoutError(err));
        setCardError(describeJoinError(err).message);
      }
      processingRef.current = false;
      setIsProcessing(false);
    }
  }

  if (askForPassword) {
    return (
      <div class="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full">
          <SetPasswordForm
            title="Welcome to AI Playground!"
            subtitle='Set a password for your account, or skip and use "Forgot password" later.'
            onComplete={async () => window.location.assign('/community')}
            onSkip={async () => window.location.assign('/community')}
          />
        </div>
      </div>
    );
  }

  if (joined) {
    return (
      <div class="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div class="text-center">
          <svg width="56" height="56" viewBox="0 0 56 56" fill="none" class="mx-auto mb-4">
            <circle cx="28" cy="28" r="28" fill="#16a34a" />
            <path
              d="M17 28.5l7.5 7.5 14.5-14.5"
              stroke="#fff"
              stroke-width="3.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">Welcome to AI Playground!</h2>
          <p class="text-gray-600">Loading your community...</p>
        </div>
      </div>
    );
  }

  const benefits = [
    { text: '4 Live Calls per Month', desc: 'Strategy, support, co-working, and connection' },
    { text: 'Hands-On AI Workshops', desc: 'Learn by doing and experimenting' },
    {
      text: 'Monthly Business-Building Courses',
      desc: 'Focused, high-impact, and track-based',
    },
    { text: 'Resource Library', desc: '60+ trainings at your fingertips' },
    { text: 'Supportive Community', desc: 'Ask questions, share wins, and stay inspired' },
  ];

  return (
    <div class="min-h-screen bg-gray-50">
      <div class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="max-w-3xl mx-auto flex items-center justify-between">
          <MiraseeLogo href="/community/courses" />
          {!user && (
            <a
              href={LOGIN_URL}
              class="text-sm text-indigo-600 hover:text-indigo-800 font-medium no-underline"
            >
              Log in
            </a>
          )}
          {user && (
            <div class="flex items-center gap-3 text-sm">
              <span class="text-gray-500">{user.email}</span>
              <a
                href="/logout"
                class="text-indigo-600 hover:text-indigo-800 font-medium no-underline"
              >
                Log out
              </a>
            </div>
          )}
        </div>
      </div>

      <div class="max-w-3xl mx-auto px-4 py-12">
        <div class="text-center mb-10">
          <h1 class="text-3xl font-bold text-gray-900 mb-3">AI Playground</h1>
          <p class="text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
            Build momentum. Monetize your expertise. Grow a business that works for you.
          </p>
        </div>

        <div class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="p-8">
            <p class="text-gray-700 leading-relaxed mb-6">
              AI Playground is where practical learning meets real progress — where using AI to grow
              your business becomes hands-on, supported, and even fun. You'll find the structure,
              tools, and community to help you move forward with clarity and confidence — using AI
              as a thinking partner along the way.
            </p>

            <h2 class="text-lg font-bold text-gray-900 mb-4">What's included:</h2>
            <ul class="flex flex-col gap-3 mb-8">
              {benefits.map((b) => (
                <li key={b.text} class="flex items-start gap-3">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    class="flex-shrink-0 mt-0.5"
                  >
                    <circle cx="11" cy="11" r="11" fill="#dbeafe" />
                    <path
                      d="M7 11.5l2.5 2.5 5.5-5.5"
                      stroke="#013EA6"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <div>
                    <span class="font-semibold text-gray-900">{b.text}</span>
                    <span class="text-gray-500"> — {b.desc}</span>
                  </div>
                </li>
              ))}
            </ul>

            {aipPrice && options.length > 1 && (
              <div class="border-t border-gray-100 pt-6">
                <p class="text-sm font-semibold text-gray-900 mb-2">Choose your currency</p>
                <div class="flex gap-2">
                  {options.map((option) => {
                    const selected = option.currency === aipPrice.currency;
                    return (
                      <button
                        key={option.currency}
                        type="button"
                        disabled={switchingCurrency || isProcessing || creatingAccount}
                        onClick={() => selectCurrency(option.currency)}
                        class={`flex-1 py-3 px-4 rounded-lg cursor-pointer text-sm font-semibold ${
                          selected
                            ? 'border-2 border-blue-700 bg-blue-50 text-blue-900'
                            : 'border border-gray-300 bg-white text-gray-700'
                        }`}
                      >
                        {currencyOptionLabel(option)}
                      </button>
                    );
                  })}
                </div>
                {currencyError && (
                  <p role="alert" class="mt-2 text-sm text-red-600">
                    {currencyError}
                  </p>
                )}
              </div>
            )}

            {aipPrice && !showCardForm && !showAccountForm && (
              <div class="border-t border-gray-100 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    if (!user) {
                      setShowAccountForm(true);
                    } else if (canOneClick) {
                      setShowConfirm(true);
                    } else {
                      setShowCardForm(true);
                    }
                  }}
                  class="w-full py-4 px-6 rounded-xl border-none text-lg font-bold cursor-pointer text-center"
                  style="background: linear-gradient(135deg, #ff6b00 0%, #ffc600 100%); color: #1a1a1a"
                >
                  {freeTrialDays
                    ? `Start your free ${formatTrialPeriod(freeTrialDays)} trial`
                    : `${joinVerb} AI Playground`}
                </button>
                <p class="text-sm text-gray-500 mt-3 text-center">
                  {freeTrialDays ? (
                    `${zeroDisplay} today, then ${priceDisplay}${interval} after ${formatTrialPeriod(freeTrialDays)}`
                  ) : (
                    <AipFoundingRatePrice price={aipPrice} />
                  )}
                  {' · Cancel anytime'}
                </p>
              </div>
            )}

            {aipPrice && showAccountForm && (
              <form class="border-t border-gray-100 pt-6" onSubmit={handleCreateAccount}>
                <h3 class="text-lg font-bold text-gray-900 mb-1">Create your account</h3>
                <p class="text-sm text-gray-500 mb-4">
                  {freeTrialDays ? (
                    `${zeroDisplay} today, then ${priceDisplay}${interval} after ${formatTrialPeriod(freeTrialDays)}. Cancel anytime.`
                  ) : (
                    <>
                      <AipFoundingRatePrice price={aipPrice} />
                      {' · Cancel anytime'}
                    </>
                  )}
                </p>
                <label class="block text-sm font-medium text-gray-700 mb-1" for="join-name">
                  Name
                </label>
                <input
                  id="join-name"
                  type="text"
                  autocomplete="name"
                  value={accountName}
                  onInput={(e) => setAccountName(e.currentTarget.value)}
                  disabled={creatingAccount}
                  class="ruz-input block w-full mb-4 py-3"
                />
                <label class="block text-sm font-medium text-gray-700 mb-1" for="join-email">
                  Email
                </label>
                <input
                  id="join-email"
                  type="email"
                  autocomplete="email"
                  value={accountEmail}
                  onInput={(e) => setAccountEmail(e.currentTarget.value)}
                  disabled={creatingAccount}
                  class="ruz-input block w-full mb-4 py-3"
                  data-private
                />
                {accountError && (
                  <div
                    role="alert"
                    class="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
                  >
                    {accountError.message}
                    {accountError.loginLink && (
                      <>
                        {' '}
                        <a href={LOGIN_URL} class="underline font-medium">
                          Log in
                        </a>
                      </>
                    )}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={creatingAccount}
                  class="w-full py-4 px-6 rounded-xl border-none text-lg font-bold cursor-pointer text-center disabled:opacity-50"
                  style="background: linear-gradient(135deg, #ff6b00 0%, #ffc600 100%); color: #1a1a1a"
                >
                  {creatingAccount ? 'Creating account...' : 'Continue to payment'}
                </button>
                {!user && (
                  <p class="text-sm text-gray-500 mt-3 text-center">
                    Already have an account?{' '}
                    <a href={LOGIN_URL} class="underline">
                      Log in
                    </a>
                  </p>
                )}
              </form>
            )}

            {aipPrice && showCardForm && (
              <div class="border-t border-gray-100 pt-6">
                <h3 class="text-lg font-bold text-gray-900 mb-1">
                  {freeTrialDays
                    ? `Start your free ${formatTrialPeriod(freeTrialDays)} trial`
                    : `${joinVerb} AI Playground`}
                </h3>
                <p class="text-sm text-gray-500 mb-4">
                  {freeTrialDays ? (
                    `${zeroDisplay} today, then ${priceDisplay}${interval} after ${formatTrialPeriod(freeTrialDays)}. Cancel anytime.`
                  ) : (
                    <>
                      <AipFoundingRatePrice price={aipPrice} />
                      {' · Cancel anytime'}
                    </>
                  )}
                </p>
                <div class="mb-4">
                  <StripeInput
                    priceId={aipPrice.id}
                    customerName={user?.name || ''}
                    onReady={setStripeCtx}
                    collectAddress={requiresAddress ?? true}
                    disabled={isProcessing}
                    amount={aipPrice.priceInCents}
                    currency={aipPrice.currency}
                    mode={
                      freeTrialDays
                        ? 'setup'
                        : aipPrice.paymentType === 'subscription' ||
                            aipPrice.paymentType === 'paymentplan'
                          ? 'subscription'
                          : 'payment'
                    }
                  />
                  {cardError && (
                    <div
                      role="alert"
                      class="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
                    >
                      {cardError}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleCardSubmit}
                  disabled={isProcessing || !stripeCtx}
                  class="w-full py-4 px-6 rounded-xl border-none text-lg font-bold cursor-pointer text-center disabled:opacity-50"
                  style="background: linear-gradient(135deg, #ff6b00 0%, #ffc600 100%); color: #1a1a1a"
                >
                  {isProcessing ? 'Processing...' : 'Confirm & Pay'}
                </button>
              </div>
            )}

            {!aipPrice && (
              <div class="border-t border-gray-100 pt-6 text-center">
                <p class="text-gray-700 mb-1">We could not load AI Playground pricing.</p>
                <p class="text-sm text-gray-500 mb-4">
                  Refresh the page, or email{' '}
                  <a href="mailto:support@mirasee.com" class="underline">
                    support@mirasee.com
                  </a>{' '}
                  and we will sign you up.
                </p>
                <a
                  href="/community/courses"
                  class="inline-block py-3 px-8 rounded-lg bg-indigo-600 text-white font-semibold no-underline"
                >
                  Browse Courses
                </a>
              </div>
            )}
          </div>
        </div>

        {user && (
          <p class="text-center mt-6">
            <a href="/courses" class="text-sm text-gray-500 hover:text-gray-700 underline">
              Not right now — take me to my courses
            </a>
          </p>
        )}
      </div>

      {showConfirm && aipPrice && (
        <ConfirmModal
          isProcessing={isProcessing}
          onClose={() => {
            setShowConfirm(false);
            setConfirmError(undefined);
          }}
        >
          <h3 id="confirm-heading" class="text-xl font-bold text-gray-900 mb-2">
            Confirm your membership
          </h3>
          <p class="text-gray-600 mb-4 leading-relaxed">
            {freeTrialDays ? (
              <>
                You'll start a free {formatTrialPeriod(freeTrialDays)} trial of AI Playground. After{' '}
                {trialEndDate(freeTrialDays)}, your card will be charged{' '}
                <strong>
                  {priceDisplay}
                  {interval}
                </strong>
                .
              </>
            ) : (
              <>
                Your card will be charged{' '}
                <strong>
                  {priceDisplay}
                  {interval}
                </strong>{' '}
                for AI Playground.
              </>
            )}
          </p>
          {canOneClick && cardInfo?.hasCard && (
            <div class="flex items-center justify-center gap-3 py-3 px-4 bg-gray-50 rounded-lg mb-4">
              <CardIcon brand={cardInfo.brand} />
              <span class="text-sm text-gray-700">
                <span class="capitalize font-medium">{cardInfo.brand}</span>
                {' ending in '}
                <span class="font-semibold">{cardInfo.last4}</span>
              </span>
            </div>
          )}
          {confirmError && (
            <p class="text-sm text-red-600 mb-4">
              {confirmError.message}
              {confirmError.communityLink && (
                <>
                  {' '}
                  <a href="/community" class="underline">
                    Go to AI Playground
                  </a>
                </>
              )}
            </p>
          )}
          {isProcessing ? (
            <div class="py-6">
              <div class="flex items-center justify-center gap-3 mb-3">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  class="animate-spin"
                  style="color: #013EA6"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="2.5"
                    fill="none"
                    opacity="0.2"
                  />
                  <path
                    d="M12 2a10 10 0 0 1 10 10"
                    stroke="currentColor"
                    stroke-width="2.5"
                    fill="none"
                    stroke-linecap="round"
                  />
                </svg>
                <span class="text-sm font-medium text-gray-700">Processing your payment...</span>
              </div>
              <p class="text-xs text-gray-400">
                This may take a few seconds. Please don't close this window.
              </p>
            </div>
          ) : (
            <div class="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowConfirm(false);
                  setConfirmError(undefined);
                }}
                class="flex-1 py-3 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                class="flex-1 py-3 rounded-lg border-none text-white font-semibold cursor-pointer"
                style="background: #013EA6"
              >
                Confirm
              </button>
            </div>
          )}
        </ConfirmModal>
      )}
    </div>
  );
}

function Page({ state }: LoadedProps<typeof load>) {
  return (
    <NotEnrolledPage
      aipPrice={state.aipPrice}
      requiresAddress={state.aipRequiresAddress}
      savedAddress={state.aipSavedAddress}
      hadMembership={state.hadMembership}
      currencyOptions={state.aipCurrencyOptions}
    />
  );
}

export const route = defCoursesRoute({
  load,
  Page,
  isPublic: true,
});
